import {
  Assignment as AssignmentIcon,
  AttachFile as AttachFileIcon,
  CalendarToday as CalendarIcon,
  Delete as DeleteIcon,
  Flag as FlagIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography
} from '@mui/material';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { useEffect, useRef, useState } from 'react';
import { storage } from '../firebase';
import { CreateDietProgramRequest, DietProgram, dietProgramApi } from '../services/dietProgramApi';
import { colors } from '../theme';
import CancelButton from './CancelButton';

interface DietProgramModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (programData: CreateDietProgramRequest) => void;
  clients: ClientData[];
  dietitianId?: string;
  editingProgram?: DietProgram | null;
}

interface ClientData {
  id: string;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  age: number;
  gender: string;
  goal: string;
  notes: string;
  createdAt: Date;
}

interface DietProgramData {
  dietitianId: string;
  clientId: string;
  clientName: string;
  programName: string;
  programType: string;
  startDate: string;
  endDate: string;
  description?: string;
  goals?: string;
  restrictions?: string;
  notes?: string;
  status?: string;
  targetWeight?: number;
  currentWeight?: number;
  pdfUrl?: string;
}

const DietProgramModal: React.FC<DietProgramModalProps> = ({
  open,
  onClose,
  onSave,
  clients,
  dietitianId = '',
  editingProgram,
}) => {
  const sectionStyle = {
    p: 2.5,
    borderRadius: 2,
    bgcolor: 'white',
  };

  const [formData, setFormData] = useState<DietProgramData>({
    dietitianId: dietitianId,
    clientId: '',
    clientName: '',
    programName: '',
    programType: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split('T')[0],
    description: '',
    goals: '',
    restrictions: '',
    notes: '',
    status: 'Aktif',
    targetWeight: undefined,
    currentWeight: undefined,
    pdfUrl: undefined,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedClient, setSelectedClient] = useState<ClientData | null>(null);
  const [uploading, setUploading] = useState(false);
  const [pdfFileName, setPdfFileName] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [existingActivePrograms, setExistingActivePrograms] = useState<DietProgram[]>([]);

  // Düzenleme modunda formData'yı doldur
  useEffect(() => {
    if (editingProgram && open) {
      const formatDate = (dateStr: string) => {
        if (!dateStr) return new Date().toISOString().split('T')[0];
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return new Date().toISOString().split('T')[0];
        return d.toISOString().split('T')[0];
      };

      setFormData({
        dietitianId: editingProgram.dietitianId || dietitianId,
        clientId: editingProgram.clientId || '',
        clientName: editingProgram.clientName || '',
        programName: editingProgram.programName || '',
        programType: editingProgram.programType || '',
        startDate: formatDate(editingProgram.startDate),
        endDate: formatDate(editingProgram.endDate),
        description: editingProgram.description || '',
        goals: editingProgram.goals || '',
        restrictions: editingProgram.restrictions || '',
        notes: editingProgram.notes || '',
        status: editingProgram.status || 'Aktif',
        targetWeight: editingProgram.targetWeight,
        currentWeight: editingProgram.currentWeight,
        pdfUrl: editingProgram.pdfUrl,
      });

      // Danışanı seç
      const selectedClient = clients.find(c => c.id === editingProgram.clientId);
      setSelectedClient(selectedClient || null);

      // Yüklü dosya adını ayarla
      if (editingProgram.pdfUrl) {
        setPdfFileName('Mevcut dosya');
      }
    } else if (open && !editingProgram) {
      // Yeni program modunda form'u temizle
      setFormData({
        dietitianId: dietitianId,
        clientId: '',
        clientName: '',
        programName: '',
        programType: '',
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split('T')[0],
        description: '',
        goals: '',
        restrictions: '',
        notes: '',
        status: 'Aktif',
        targetWeight: undefined,
        currentWeight: undefined,
        pdfUrl: undefined,
      });
      setSelectedClient(null);
      setPdfFileName(null);
      setExistingActivePrograms([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [editingProgram, open, clients,dietitianId]);

  const handleInputChange =
    (field: keyof DietProgramData) =>
    (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any
    ) => {
      const value = event.target.value;
      setFormData((prev) => ({ ...prev, [field]: value }));

      if (errors[field]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    };

  const [hasActiveProgram, setHasActiveProgram] = useState(false);

  // Danışan değiştiğinde aktif program kontrolü
  useEffect(() => {
    const checkActiveProgram = async () => {
      if (formData.clientId && formData.status === 'Aktif') {
        try {
          const programs = await dietProgramApi.getDietProgramsByClient(formData.clientId);
          const activePrograms = programs.filter(p => p.status === 'Aktif');
          setHasActiveProgram(activePrograms.length > 0);
        } catch (err) {
          console.error('Error checking active programs:', err);
          setHasActiveProgram(false);
        }
      } else {
        setHasActiveProgram(false);
      }
    };

    checkActiveProgram();
  }, [formData.clientId, formData.status]);

  const handleClientChange = (event: any) => {
    const clientId = event.target.value;
    const selectedClient = clients.find((client) => client.id === clientId);

    setSelectedClient(selectedClient || null);
    setFormData((prev) => ({
      ...prev,
      clientId,
      clientName: selectedClient
        ? `${selectedClient.firstName} ${selectedClient.lastName}`
        : '',
    }));

    if (errors.clientId) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.clientId;
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.clientId) {
      newErrors.clientId = 'Danışan seçiniz';
    }
    if (!formData.programName.trim()) {
      newErrors.programName = 'Diyet programı adı gereklidir';
    }
    if (!formData.programType.trim()) {
      newErrors.programType = 'Diyet programı türü seçiniz';
    }
    if (!formData.startDate) {
      newErrors.startDate = 'Başlangıç tarihi gereklidir';
    }
    if (!formData.endDate) {
      newErrors.endDate = 'Bitiş tarihi gereklidir';
    }
    if (
      formData.startDate &&
      formData.endDate &&
      new Date(formData.startDate) >= new Date(formData.endDate)
    ) {
      newErrors.endDate = 'Bitiş tarihi başlangıç tarihinden sonra olmalıdır';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    // Eğer program "Aktif" olarak işaretleniyorsa, aktif program kontrolü yap
    if (formData.status === 'Aktif' && formData.clientId) {
      try {
        const existingPrograms = await dietProgramApi.getDietProgramsByClient(formData.clientId);
        const activePrograms = existingPrograms.filter(p => p.status === 'Aktif');
        
        if (activePrograms.length > 0) {
          // Aktif program varsa onay dialog'u göster
          setExistingActivePrograms(activePrograms);
          setConfirmDialogOpen(true);
          return;
        }
      } catch (err) {
        console.error('Error checking active programs:', err);
      }
    }

    // Aktif program yoksa veya durum "Aktif" değilse direkt kaydet
    proceedWithSave();
  };

  const proceedWithSave = async () => {
    // Eğer onay verildiyse, eski aktif programları "Tamamlandı" yap
    if (existingActivePrograms.length > 0) {
      try {
        for (const program of existingActivePrograms) {
          await dietProgramApi.updateDietProgram(program.id, {
            status: 'Tamamlandı',
          });
        }
      } catch (err) {
        console.error('Error updating existing programs:', err);
      }
    }

    onSave({
      ...formData,
      dailyMealPlans: [],
    });
    
    // Form'u temizle
    setFormData({
      dietitianId: dietitianId,
      clientId: '',
      clientName: '',
      programName: '',
      programType: '',
      startDate: new Date().toISOString().split('T')[0],
      endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split('T')[0],
      description: '',
      goals: '',
      restrictions: '',
      notes: '',
      status: 'Aktif',
      targetWeight: undefined,
      currentWeight: undefined,
      pdfUrl: undefined,
    });
    setSelectedClient(null);
    setErrors({});
    setPdfFileName(null);
    setExistingActivePrograms([]);
    setConfirmDialogOpen(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  const handleClose = () => {
    setFormData({
      dietitianId: dietitianId,
      clientId: '',
      clientName: '',
      programName: '',
      programType: '',
      startDate: new Date().toISOString().split('T')[0],
      endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split('T')[0],
      description: '',
      goals: '',
      restrictions: '',
      notes: '',
      status: 'Aktif',
      targetWeight: undefined,
      currentWeight: undefined,
      pdfUrl: undefined,
    });
    setSelectedClient(null);
    setErrors({});
    setPdfFileName(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate client is selected
    if (!formData.clientId) {
      setErrors((prev) => ({
        ...prev,
        pdf: 'Önce bir danışan seçmeniz gerekiyor',
      }));
      return;
    }

    // İzin verilen formatlar: PDF, Word, Excel, görseller, metin
    const allowedTypes = [
      'application/pdf',
      'application/msword', // .doc
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
      'application/vnd.ms-excel', // .xls
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
      'image/bmp',
      'text/plain',
    ];
    const allowedExtensions = /\.(pdf|doc|docx|xls|xlsx|jpg|jpeg|png|gif|webp|bmp|txt)$/i;
    const isAllowedType = allowedTypes.includes(file.type) || allowedExtensions.test(file.name);
    if (!isAllowedType) {
      setErrors((prev) => ({
        ...prev,
        pdf: 'Desteklenen formatlar: PDF, Word, Excel, resim (JPG, PNG, GIF, WebP), metin (TXT)',
      }));
      return;
    }

    // Validate file size (max 15MB)
    if (file.size > 15 * 1024 * 1024) {
      setErrors((prev) => ({
        ...prev,
        pdf: "Dosya boyutu 15MB'dan küçük olmalıdır",
      }));
      return;
    }

    try {
      setUploading(true);
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.pdf;
        return newErrors;
      });

      // Clean file name - remove special characters and spaces
      const cleanFileName = file.name
        .replace(/[^a-zA-Z0-9.-]/g, '_')
        .replace(/\s+/g, '_')
        .toLowerCase();
      
      // Upload to Firebase Storage
      const timestamp = Date.now();
      const fileName = `diet-programs/${formData.clientId}/${timestamp}_${cleanFileName}`;
      const storageRef = ref(storage, fileName);

      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      setFormData((prev) => ({ ...prev, pdfUrl: downloadURL }));
      setPdfFileName(file.name);
    } catch (error: any) {
      console.error('Error uploading file:', error);
      let errorMessage = 'Dosya yüklenirken hata oluştu';
      
      if (error?.code === 'storage/unauthorized') {
        errorMessage = 'Yükleme yetkisi yok. Lütfen giriş yaptığınızdan emin olun.';
      } else if (error?.code === 'storage/canceled') {
        errorMessage = 'Yükleme iptal edildi';
      } else if (error?.code === 'storage/unknown') {
        errorMessage = 'Bilinmeyen bir hata oluştu. Lütfen tekrar deneyin.';
      } else if (error?.message?.includes('CORS')) {
        errorMessage = 'CORS hatası. Lütfen Firebase Storage CORS ayarlarını kontrol edin.';
      }
      
      setErrors((prev) => ({
        ...prev,
        pdf: errorMessage,
      }));
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePdf = () => {
    setFormData((prev) => ({ ...prev, pdfUrl: undefined }));
    setPdfFileName(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' },
      }}
    >
      <DialogTitle
        sx={{
          pb: 2,
          pt: 3,
          px: 3,
          background: 'linear-gradient(135deg, #43A047 0%, #2E7D32 100%)',
          color: 'white',
        }}
      >
        <Typography variant="h5" component="div" fontWeight={700}>
          {editingProgram ? 'Diyet Programını Düzenle' : 'Diyet Programı Hazırla'}
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
          {editingProgram 
            ? 'Diyet programı bilgilerini güncelleyin ve kaydedin.'
            : 'Danışan seç, tarihleri ve hedefleri gir, hızlıca diyet programı oluştur.'}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ bgcolor: '#fafbfc', p: 2, mt: 1 }}>
        <Box sx={{ display: 'grid', gap: 2.5 }}>
          <Paper elevation={0} sx={sectionStyle}>
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              mb={2}
              sx={{
                pb: 1.5,
                borderBottom: `2px solid ${colors.primary.light}`,
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  bgcolor: colors.primary.light,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <PersonIcon sx={{ fontSize: 20, color: colors.primary.main }} />
              </Box>
              <Typography variant="h6" fontWeight={600}>
                Danışan
              </Typography>
            </Box>

            <FormControl fullWidth required error={!!errors.clientId}>
              <InputLabel>Danışan Seçiniz</InputLabel>
              <Select
                value={formData.clientId}
                onChange={handleClientChange}
                label="Danışan Seçiniz"
              >
                {clients.map((client) => (
                  <MenuItem key={client.id} value={client.id}>
                    {client.firstName} {client.lastName} - {client.email}
                  </MenuItem>
                ))}
              </Select>
              {errors.clientId && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, ml: 1.5 }}
                >
                  {errors.clientId}
                </Typography>
              )}
            </FormControl>

            {selectedClient && (
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  border: '1px solid #e7ecf0',
                  bgcolor: '#fff',
                  display: 'grid',
                  gap: 0.5,
                }}
              >
                <Typography variant="subtitle2" fontWeight={700}>
                  {selectedClient.firstName} {selectedClient.lastName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Yaş: {selectedClient.age} • Cinsiyet: {selectedClient.gender}
                </Typography>
                {selectedClient.goal && (
                  <Typography variant="body2" color="text.secondary">
                    Hedef: {selectedClient.goal}
                  </Typography>
                )}
                {selectedClient.notes && (
                  <Typography variant="body2" color="text.secondary">
                    Not: {selectedClient.notes}
                  </Typography>
                )}
                {hasActiveProgram && formData.status === 'Aktif' && (
                  <Box
                    sx={{
                      mt: 1,
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: colors.status.warning + '15',
                      border: `1px solid ${colors.status.warning}`,
                    }}
                  >
                    <Typography variant="caption" sx={{ color: colors.status.warning }} fontWeight={600}>
                      ⚠️ Bu danışanın zaten aktif bir diyet programı var. Yeni diyet programı oluşturulduğunda eski diyet programı otomatik olarak "Tamamlandı" olarak işaretlenecektir.
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Paper>

          <Paper elevation={0} sx={sectionStyle}>
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              mb={2}
              sx={{
                pb: 1.5,
                borderBottom: `2px solid ${colors.primary.light}`,
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  bgcolor: colors.primary.light,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <AssignmentIcon
                  sx={{ fontSize: 20, color: colors.primary.main }}
                />
              </Box>
              <Typography variant="h6" fontWeight={600}>
                Diyet Programı Bilgileri
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)',
                },
                gap: 2,
              }}
            >
              <TextField
                label="Diyet Programı Adı"
                value={formData.programName}
                onChange={handleInputChange('programName')}
                error={!!errors.programName}
                helperText={errors.programName}
              />

              <FormControl fullWidth required error={!!errors.programType}>
                <InputLabel>Diyet Programı Türü</InputLabel>
                <Select
                  value={formData.programType}
                  onChange={handleInputChange('programType')}
                  label="Diyet Programı Türü"
                >
                  <MenuItem value="Sporcu Beslenmesi">
                    Sporcu Beslenmesi
                  </MenuItem>
                  <MenuItem value="Kilo Verme">Kilo Verme</MenuItem>
                  <MenuItem value="Kilo Alma">Kilo Alma</MenuItem>
                  <MenuItem value="Klinik Beslenme">Klinik Beslenme</MenuItem>
                  <MenuItem value="Hamilelik">Hamilelik</MenuItem>
                </Select>
                {errors.programType && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, ml: 1.5 }}
                  >
                    {errors.programType}
                  </Typography>
                )}
              </FormControl>

              <TextField
                label="Durum"
                select
                value={formData.status}
                onChange={handleInputChange('status')}
              >
                <MenuItem value="Aktif">Aktif</MenuItem>
                <MenuItem value="Tamamlandı">Tamamlandı</MenuItem>
                <MenuItem value="İptal Edildi">İptal Edildi</MenuItem>
              </TextField>

              <TextField
                label="Başlangıç Tarihi"
                type="date"
                value={formData.startDate}
                onChange={handleInputChange('startDate')}
                error={!!errors.startDate}
                helperText={errors.startDate}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarIcon
                        sx={{ fontSize: 18, color: colors.text.secondary }}
                      />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Bitiş Tarihi"
                type="date"
                value={formData.endDate}
                onChange={handleInputChange('endDate')}
                error={!!errors.endDate}
                helperText={errors.endDate}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarIcon
                        sx={{ fontSize: 18, color: colors.text.secondary }}
                      />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Hedef Kilo (kg)"
                type="number"
                value={formData.targetWeight || ''}
                onChange={handleInputChange('targetWeight')}
                InputProps={{ inputProps: { min: 0 } }}
              />

              <TextField
                label="Güncel Kilo (kg)"
                type="number"
                value={formData.currentWeight || ''}
                onChange={handleInputChange('currentWeight')}
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Box>
          </Paper>

          <Paper elevation={0} sx={sectionStyle}>
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              mb={2}
              sx={{
                pb: 1.5,
                borderBottom: `2px solid ${colors.primary.light}`,
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  bgcolor: colors.primary.light,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <FlagIcon sx={{ fontSize: 20, color: colors.primary.main }} />
              </Box>
              <Typography variant="h6" fontWeight={600}>
                Açıklama ve Notlar
              </Typography>
            </Box>

            <Box sx={{ display: 'grid', gap: 1.5 }}>
              <TextField
                label="Diyet Programı Açıklaması"
                multiline
                minRows={2}
                value={formData.description}
                onChange={handleInputChange('description')}
                placeholder="Diyet programının genel amacı ve içeriği..."
              />

              <TextField
                label="Hedefler"
                multiline
                minRows={2}
                value={formData.goals}
                onChange={handleInputChange('goals')}
                placeholder="Örn: Haftalık 0.5kg kayıp, kas kütlesini koruma..."
              />

              <TextField
                label="Kısıtlamalar"
                multiline
                minRows={2}
                value={formData.restrictions}
                onChange={handleInputChange('restrictions')}
                placeholder="Örn: Gluten, laktoz, alerjenler..."
              />

              <TextField
                label="Ek Notlar"
                multiline
                minRows={2}
                value={formData.notes}
                onChange={handleInputChange('notes')}
                placeholder="Takip notları, hatırlatmalar..."
              />
            </Box>
          </Paper>

          <Paper elevation={0} sx={sectionStyle}>
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              mb={2}
              sx={{
                pb: 1.5,
                borderBottom: `2px solid ${colors.primary.light}`,
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  bgcolor: colors.primary.light,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <AttachFileIcon sx={{ fontSize: 20, color: colors.primary.main }} />
              </Box>
              <Typography variant="h6" fontWeight={600}>
                Ek Dosya (Opsiyonel)
              </Typography>
            </Box>

            <Box sx={{ display: 'grid', gap: 1.5 }}>
              <input
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,image/jpeg,image/png,image/gif,image/webp,image/bmp,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain"
                ref={fileInputRef}
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              
              {!formData.pdfUrl ? (
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<AttachFileIcon />}
                  disabled={uploading || !formData.clientId}
                  sx={{
                    borderColor: colors.primary.main,
                    color: colors.primary.main,
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: colors.primary.dark,
                      bgcolor: colors.alpha.primary10,
                    },
                    '&:disabled': {
                      borderColor: colors.border.light,
                      color: colors.text.secondary,
                    },
                  }}
                >
                  {uploading ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Yükleniyor...
                    </>
                  ) : (
                    'Dosya Yükle'
                  )}
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,image/*,.txt"
                    hidden
                    onChange={handleFileSelect}
                  />
                </Button>
              ) : (
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 1.5,
                    border: `1px solid ${colors.border.light}`,
                    bgcolor: colors.background.light,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <AttachFileIcon sx={{ fontSize: 32, color: '#d32f2f' }} />
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {pdfFileName || 'Yüklü dosya'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Dosya başarıyla yüklendi
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton
                    onClick={handleRemovePdf}
                    size="small"
                    sx={{
                      width: 'fit-content',
                      color: colors.status.error,
                      '&:hover': {
                        bgcolor: 'rgba(211, 47, 47, 0.1)',
                      },
                    }}
                  >
                    <DeleteIcon sx={{ width:'fit-content'}} />
                  </IconButton>
                </Box>
              )}
              
              {errors.pdf && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, ml: 1.5 }}
                >
                  {errors.pdf}
                </Typography>
              )}
              
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                {!formData.clientId 
                  ? 'Dosya yüklemek için önce bir danışan seçmeniz gerekiyor.'
                  : 'Maks. 15MB. PDF, Word, Excel, resim (JPG, PNG, GIF, WebP) veya TXT.'}
              </Typography>
            </Box>
          </Paper>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          pt: 2,
          bgcolor: '#fafbfc',
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <CancelButton onClick={handleClose} />
        <Button
          onClick={handleSave}
          variant="contained"
          sx={{
            bgcolor: colors.primary.main,
            color: 'white',
            textTransform: 'none',
            px: 4,
            py: 1,
            borderRadius: 1.5,
            boxShadow: '0 4px 12px rgba(67,160,71,0.3)',
            '&:hover': {
              bgcolor: colors.hover.primary,
              boxShadow: '0 6px 16px rgba(67,160,71,0.4)',
            },
          }}
        >
          {editingProgram ? 'Diyet Programı Güncelle' : 'Diyet Programı Oluştur'}
        </Button>
      </DialogActions>

      {/* Onay Dialog'u - Aktif Diyet Programı Kontrolü */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: colors.status.warning }}>
          ⚠️ Aktif Diyet Programı Uyarısı
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Bu danışanın zaten aktif bir diyet programı bulunmaktadır:
          </Typography>
          {existingActivePrograms.map((program) => (
            <Paper
              key={program.id}
              elevation={0}
              sx={{
                p: 2,
                mb: 1.5,
                bgcolor: colors.background.light,
                border: `1px solid ${colors.border.light}`,
                borderRadius: 1.5,
              }}
            >
              <Typography variant="subtitle2" fontWeight={600}>
                {program.programName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {program.programType} •{' '}
                {new Date(program.startDate).toLocaleDateString('tr-TR')} -{' '}
                {new Date(program.endDate).toLocaleDateString('tr-TR')}
              </Typography>
            </Paper>
          ))}
          <Typography variant="body2" sx={{ mt: 2, color: colors.text.secondary }}>
            Yeni diyet programı oluşturulduğunda mevcut aktif diyet program(lar)ı otomatik olarak{' '}
            <strong>"Tamamlandı"</strong> olarak işaretlenecektir.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: colors.status.warning, fontWeight: 600 }}>
            Devam etmek istediğinizden emin misiniz?
          </Typography>
        </DialogContent>
        <DialogActions>
          <CancelButton
            onClick={() => {
              setConfirmDialogOpen(false);
              setExistingActivePrograms([]);
            }}
          />
          <Button
            onClick={proceedWithSave}
            variant="contained"
            sx={{
              bgcolor: colors.status.warning,
              color: 'white',
              textTransform: 'none',
              '&:hover': {
                bgcolor: colors.status.warning + 'dd',
              },
            }}
          >
            Evet, Devam Et
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default DietProgramModal;
